import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { 
  ArrowRight, CheckCircle, Phone, Clock, TrendingUp,
  Building, Heart, Shield, Banknote, ChevronDown, ChevronUp,
  PlayCircle
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import {
  industriesOverview,
  healthcareUseCases,
  insuranceUseCases,
  financialServicesUseCases,
  enterpriseUseCases,
  industryStats
} from '../data/industriesData';

const Industries = () => {
  const [expandedUseCase, setExpandedUseCase] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('healthcare');

  const industryIcons = {
    healthcare: Heart,
    insurance: Shield,
    financial: Banknote,
    enterprise: Building
  };

  const allUseCases = {
    healthcare: healthcareUseCases,
    insurance: insuranceUseCases,
    financial: financialServicesUseCases,
    enterprise: enterpriseUseCases
  };

  const toggleUseCase = (index) => {
    setExpandedUseCase(expandedUseCase === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-slate-900 to-gray-900">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-32 px-4 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-purple-900/20" />
        
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-8 leading-tight">
            Voice Agents for
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400">
              Every Industry
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12">
            Autonomous workflows that complete enterprise tasks across healthcare, insurance, financial services, and operations.
          </p>
        </div>
      </section>

      {/* INDUSTRIES GRID */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industriesOverview.map((industry, index) => {
              const Icon = industryIcons[industry.path.split('/').pop().replace('-', '')];
              return (
                <Card 
                  key={index} 
                  className="bg-gray-900/50 border border-gray-800 p-8 hover:border-blue-500/50 transition-all cursor-pointer"
                  onClick={() => setSelectedIndustry(industry.path.split('/').pop().replace('-', ''))}
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      {Icon && <Icon className="text-blue-400" size={28} />}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{industry.title}</h3>
                      <p className="text-blue-400 font-semibold">{industry.tagline}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-6">{industry.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {industry.workflows.map((workflow, i) => (
                      <div key={i} className="flex items-center text-sm text-gray-400">
                        <CheckCircle className="text-blue-400 mr-2 flex-shrink-0" size={16} />
                        <span>{workflow}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button variant="outline" className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
                    Explore Use Cases <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRY DEEP DIVE */}
      <section className="py-20 px-4 bg-black/40 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          {/* Industry Selector */}
          <div className="flex flex-wrap gap-4 justify-center mb-16">
            {Object.keys(allUseCases).map((industry) => (
              <button
                key={industry}
                onClick={() => setSelectedIndustry(industry)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  selectedIndustry === industry
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                {industry.charAt(0).toUpperCase() + industry.slice(1)}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {Object.entries(industryStats[selectedIndustry] || {}).map(([key, stat], index) => (
              <div key={index} className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Use Cases */}
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              Production Use Cases
            </h2>
            
            {allUseCases[selectedIndustry]?.map((useCase, index) => (
              <Card key={index} className="bg-gray-900/50 border border-gray-800 overflow-hidden">
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-800/30 transition-all"
                  onClick={() => toggleUseCase(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm text-blue-400 font-semibold mb-2">{useCase.category}</div>
                      <h3 className="text-2xl font-bold text-white mb-3">{useCase.title}</h3>
                      <p className="text-gray-400">{useCase.problem}</p>
                    </div>
                    <button className="ml-4 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800">
                      {expandedUseCase === index ? (
                        <ChevronUp className="text-blue-400" size={24} />
                      ) : (
                        <ChevronDown className="text-gray-400" size={24} />
                      )}
                    </button>
                  </div>
                </div>

                {expandedUseCase === index && (
                  <div className="p-6 pt-0 border-t border-gray-800 space-y-6">
                    {/* Solution */}
                    <div>
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center">
                        <Phone className="text-blue-400 mr-2" size={20} />
                        The Voice Agent Solution
                      </h4>
                      <p className="text-gray-300">{useCase.solution}</p>
                    </div>

                    {/* Workflow */}
                    <div>
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center">
                        <PlayCircle className="text-blue-400 mr-2" size={20} />
                        Agent Workflow
                      </h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {useCase.workflow.map((step, i) => (
                          <div key={i} className="flex items-start space-x-3 bg-gray-800/30 p-3 rounded-lg">
                            <div className="text-blue-400 font-mono text-sm mt-0.5">{String(i + 1).padStart(2, '0')}</div>
                            <div className="text-sm text-gray-300">{step}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Outcomes */}
                    <div>
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center">
                        <TrendingUp className="text-green-400 mr-2" size={20} />
                        Measured Outcomes
                      </h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {useCase.outcomes.map((outcome, i) => (
                          <div key={i} className="flex items-start space-x-2">
                            <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={18} />
                            <span className="text-gray-300">{outcome}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Technical Depth */}
                    <div>
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center">
                        <Clock className="text-purple-400 mr-2" size={20} />
                        Technical Implementation
                      </h4>
                      <p className="text-sm text-gray-400 bg-gray-800/30 p-4 rounded-lg font-mono">
                        {useCase.technicalDepth}
                      </p>
                    </div>

                    {/* CTA */}
                    <div className="pt-4 border-t border-gray-800">
                      <Link to="/contact">
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                          Build This Agent <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHY VOICE AGENTS */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            Why Voice Agents, Not Chatbots
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div className="bg-gray-900/50 border border-gray-800 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-400 mb-4">Traditional Chatbots</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start"><span className="mr-2">•</span>Answer questions</li>
                <li className="flex items-start"><span className="mr-2">•</span>Provide information</li>
                <li className="flex items-start"><span className="mr-2">•</span>Require users to type</li>
                <li className="flex items-start"><span className="mr-2">•</span>Limited to one channel</li>
                <li className="flex items-start"><span className="mr-2">•</span>Can't make outbound calls</li>
                <li className="flex items-start"><span className="mr-2">•</span>Single interaction scope</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-blue-400 mb-4">Vedara Voice Agents</h3>
              <ul className="space-y-3 text-blue-300">
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Complete workflows</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Execute tasks</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Natural voice conversation</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Make and receive calls</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Navigate IVR systems</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Multi-day persistence</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} />Update enterprise systems</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 bg-black/40 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            What workflow should we automate first?
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Tell us about your highest-volume, highest-friction workflow.
            <br />
            We'll show you what a Voice Agent can do.
          </p>

          <Link to="/contact">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-8 text-xl rounded-xl shadow-lg shadow-blue-500/50">
              Build a Voice Agent <ArrowRight className="ml-2" size={24} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Industries;
